"use client";

import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EntityAccessList,
  EntityPicker,
  H6,
  type EntityAccessItem,
  type EntityOption,
} from "@focal/ui-kit";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllUsers,
  USER_GET,
  Permission,
  getEntityPermissions,
  getGroups,
  createPermission,
  deletePermission,
  DELIVERABLE_LIST,
  DELIVERABLE_TEMPLATE_LIST,
  ENTITY_PERMISSIONS_GET,
  GROUP_PERMISSIONS_GET,
} from "@/api";
import { useSessionContext } from "@/context";
import { toast } from "sonner";
import { showErrorToast } from "@/lib/errorUtils";
import { EntityType, PermissionLevel } from "@/types/entity";

/**
 * The single sharing dialog for the whole app. It is resource-parameterised via
 * `entityId` + `entityType`, so conversations, clients, deliverables and
 * deliverable templates all share one implementation: the same user/group
 * picker, the same permission level chosen by the sharer, and the same
 * whole-firm option (which is simply a group in the picker).
 *
 * Do not add a second sharing component for a new entity type — add the type to
 * `EntityType` and render this dialog with it.
 */
interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  entityType: EntityType;
  /**
   * When set, the owner's grant is locked (the server refuses to remove it) and
   * any other member may remove their own access.
   */
  ownerUserId?: string;
  /** Called after the caller removes their own access. */
  onOwnAccessRemoved?: () => void;
}

/**
 * Lists whose entries carry a sharing summary of their own, so a share or
 * unshare here leaves a stale summary cached until the list is refetched. Only
 * the deliverable lists enrich their entries this way today; the other entity
 * types read the permission query, which is invalidated below regardless.
 */
const LIST_KEYS_WITH_SHARING_SUMMARY: Partial<Record<EntityType, string>> = {
  [EntityType.DELIVERABLE]: DELIVERABLE_LIST,
  [EntityType.DELIVERABLE_TEMPLATE]: DELIVERABLE_TEMPLATE_LIST,
};

export const ShareModal = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  ownerUserId,
  onOwnAccessRemoved,
}: ShareModalProps) => {
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(
    null,
  );
  const { user } = useSessionContext();
  const queryClient = useQueryClient();

  // Fetch users in the firm
  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: [USER_GET],
    queryFn: () => getAllUsers(),
    enabled: isOpen,
  });

  // Fetch groups
  const { data: groups = [], isLoading: isLoadingGroups } = useQuery({
    queryKey: [GROUP_PERMISSIONS_GET],
    queryFn: () => getGroups(),
    enabled: isOpen,
  });

  // Fetch current permissions
  const { data: permissions = [] } = useQuery({
    queryKey: [ENTITY_PERMISSIONS_GET, entityType, entityId],
    queryFn: () => getEntityPermissions(entityType, entityId),
    enabled: isOpen && !!entityId,
  });

  /**
   * Invalidate rather than refetch this component's own query: the same
   * permission key is read by every other mounted consumer, and the list entry
   * that opened this dialog carries its own sharing summary, so refreshing only
   * the dialog would leave the list behind it on the pre-change state.
   */
  const invalidateSharing = () => {
    queryClient.invalidateQueries({
      queryKey: [ENTITY_PERMISSIONS_GET, entityType, entityId],
    });
    const listKey = LIST_KEYS_WITH_SHARING_SUMMARY[entityType];
    if (listKey) {
      queryClient.invalidateQueries({ queryKey: [listKey] });
    }
  };

  // Create permission mutation
  const createPermissionMutation = useMutation({
    mutationFn: createPermission,
    onSuccess: () => {
      toast.success("Permission added successfully");
      invalidateSharing();
      setSelectedEntity(null);
    },
    onError: (error) => {
      showErrorToast(error, "sharing", "Failed to add permission.");
    },
  });

  // Delete permission mutation
  const deletePermissionMutation = useMutation({
    mutationFn: (permission: Permission) =>
      deletePermission(entityType, permission.id),
    onSuccess: (_data, permission) => {
      toast.success("Permission removed successfully");
      invalidateSharing();
      if (permission.userId === user?.id) {
        onClose();
        onOwnAccessRemoved?.();
      }
    },
    onError: (error) => {
      showErrorToast(error, "sharing", "Failed to remove permission.");
    },
  });

  const entities: EntityOption[] = [
    ...users
      .filter((user) => !permissions.some((p) => p.userId === user.id))
      .map((user) => ({
        id: user.id,
        name: `${user.firstname} ${user.lastname}`,
        kind: "user" as const,
      })),
    ...groups
      .filter((group) => !permissions.some((p) => p.groupId === group.id))
      .map((group) => ({
        id: group.id,
        name: group.name,
        kind: "group" as const,
      })),
  ];

  // Add permission handler
  const handleAddPermission = () => {
    if (!selectedEntity || !entityId || !user?.firmId) return;

    createPermissionMutation.mutate({
      firmId: user.firmId,
      entityId: entityId,
      entityType: entityType,
      level: PermissionLevel.WRITE,
      ...(selectedEntity.kind === "user"
        ? { userId: selectedEntity.id }
        : { groupId: selectedEntity.id }),
    });
  };

  const canRemovePermission = (permission: Permission): boolean => {
    if (ownerUserId === undefined) return permission.userId !== user?.id;
    return permission.userId !== ownerUserId;
  };

  // Get entity names for existing permissions
  const getEntityName = (permission: Permission): string => {
    if (permission.userId) {
      const user = users.find((u) => u.id === permission.userId);
      return user ? `${user.firstname} ${user.lastname}` : "Unknown user";
    } else if (permission.groupId) {
      const group = groups.find((g) => g.id === permission.groupId);
      return group ? group.name : "Unknown group";
    }
    return "Unknown entity";
  };

  const accessItems: EntityAccessItem[] = permissions.map((permission) => ({
    id: permission.id,
    name: getEntityName(permission),
    kind: permission.userId ? "user" : "group",
    removable: canRemovePermission(permission),
  }));

  // Derives the title noun from the entity type, e.g. CONVERSATION ->
  // "conversation", DELIVERABLE_TEMPLATE -> "deliverable template".
  const getEntityTypeDisplay = (type: EntityType): string => {
    return type.toLowerCase().replace(/_/g, " ");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md sm:max-w-lg" data-cy="share-modal">
        <DialogHeader>
          <DialogTitle>
            Share this {getEntityTypeDisplay(entityType)}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex items-center gap-2">
            <EntityPicker
              className="min-w-0 flex-1"
              options={entities}
              loading={isLoadingUsers || isLoadingGroups}
              value={selectedEntity}
              onChange={setSelectedEntity}
              data-cy="share-modal-entity"
            />
            <Button
              onClick={handleAddPermission}
              disabled={!selectedEntity || createPermissionMutation.isPending}
              variant="outline"
              data-cy="share-modal-share-button"
            >
              Share
            </Button>
          </div>

          <div className="mt-4">
            {/*
             * `text-sm` is deliberate: this dialog predates the move into
             * /common and this heading has always rendered at 14px. H6 gives it
             * the correct semantics and weight; the size override keeps the
             * existing conversation-sharing pixels unchanged.
             */}
            <H6 as="h3" spacing="sm" className="text-sm">
              People with access
            </H6>
            <EntityAccessList
              className="h-[200px]"
              items={accessItems}
              onRemove={(item) => {
                const permission = permissions.find((p) => p.id === item.id);
                if (permission) deletePermissionMutation.mutate(permission);
              }}
              disabled={deletePermissionMutation.isPending}
              data-cy="share-modal-permission"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
